import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
    EnvelopeSimpleIcon,
    PaperPlaneTiltIcon,
    CheckCircleIcon,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [err, setErr] = useState("");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await api.post("/contact", form);
            setSuccess(true);
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="px-5 md:px-10 max-w-6xl mx-auto py-16 md:py-24" data-testid="contact-page">
            <Seo title="Contact India Reels" description="Press queries, brand collabs, creator partnerships and support — get in touch with the India Reels team." path="/contact" />
            <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                contact us
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl leading-tight">
                Bolo, <span className="gradient-text">hum sun rahe hain.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-2xl">
                Press queries, brand collabs, creator partnerships, support — drop a message and
                we&apos;ll get back within 24 hours.
            </p>

            <div className="mt-12 grid md:grid-cols-[1fr_1.4fr] gap-8 items-start">
                <aside className="space-y-4">
                    <InfoCard
                        icon={EnvelopeSimpleIcon}
                        label="Support Email"
                        value="support@india-reels.com"
                        href="mailto:support@india-reels.com"
                        testid="info-email"
                    />
                    <div className="rounded-3xl border border-white/10 bg-surface p-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-secondary font-bold mb-3">
                            Business Hours
                        </div>
                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-text-secondary">Mon — Fri</span><span>10:00 — 19:00 IST</span></div>
                            <div className="flex justify-between"><span className="text-text-secondary">Saturday</span><span>11:00 — 16:00 IST</span></div>
                            <div className="flex justify-between"><span className="text-text-secondary">Sunday</span><span>Closed</span></div>
                        </div>
                    </div>
                    <div className="rounded-3xl border border-white/10 bg-surface p-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-secondary font-bold mb-2">
                            Response time
                        </div>
                        <p className="text-sm text-white/80 leading-relaxed">
                            We typically respond within{" "}
                            <span className="text-white font-bold">24 hours</span>. For urgent
                            issues, mention <em>&quot;urgent&quot;</em> in the subject.
                        </p>
                    </div>
                </aside>

                <div className="rounded-3xl border border-white/10 bg-surface p-6 md:p-10">
                    {success ? (
                        <div className="text-center py-12 fade-up" data-testid="contact-success">
                            <div className="w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-5">
                                <CheckCircleIcon size={46} weight="fill" color="#E01A4F" />
                            </div>
                            <h2 className="font-heading font-black text-2xl md:text-3xl">Message bhej diya! 🎉</h2>
                            <p className="text-text-secondary mt-3 max-w-md mx-auto">
                                Thanks for reaching out. Our team will reply to you within 24 hours.
                            </p>
                            <button
                                onClick={() => setSuccess(false)}
                                data-testid="contact-send-another"
                                className="btn-ghost mt-6 px-5 py-2.5 text-sm"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label="Your name" testid="contact-name" value={form.name} onChange={set("name")} placeholder="Aapka naam" required />
                                <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="aap@example.com" required />
                            </div>
                            <Field label="Subject" testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="What's this about?" required />
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">Message</label>
                                <textarea
                                    data-testid="contact-message"
                                    value={form.message}
                                    onChange={set("message")}
                                    rows={6}
                                    required
                                    minLength={5}
                                    maxLength={2000}
                                    placeholder="Apni baat detail mein likho..."
                                    className="input-field mt-2 resize-none"
                                />
                                <div className="text-right text-[11px] text-text-secondary mt-1">{form.message.length}/2000</div>
                            </div>
                            {err && <div data-testid="contact-error" className="text-primary text-sm bg-primary/10 px-4 py-3 rounded-xl">{err}</div>}
                            <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60">
                                <PaperPlaneTiltIcon size={18} weight="fill" />
                                {loading ? "Sending..." : "Send Message"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, testid, ...props }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">{label}</label>
            <input data-testid={testid} className="input-field mt-2" {...props} />
        </div>
    );
}

function InfoCard({ icon: Icon, label, value, href, testid }) {
    const Inner = (
        <div className="flex items-center gap-4 rounded-3xl border border-white/10 bg-surface p-5 hover:border-white/20 transition-colors">
            <div className="w-11 h-11 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
                <Icon size={22} weight="duotone" color="#E01A4F" />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] text-text-secondary font-bold">{label}</div>
                <div className="text-sm md:text-base font-semibold truncate">{value}</div>
            </div>
        </div>
    );
    return href ? <a href={href} data-testid={testid} className="block">{Inner}</a> : <div data-testid={testid}>{Inner}</div>;
}
